import type {
    ApiClient,
    SubscriptionsUpdateRequest,
    SubscriptionsUpdateSet
} from '@tadpoles/api-client'
import {
    type ConfigurableSubscriptions,
    type Subscriptions,
    type SubscriptionTopicId,
    SubscriptionChannels,
    SubscriptionTopics
} from '@tadpoles/common'
import { UserScopedService, type UserScopedServiceScope } from './user-scoped-service-scope.js'

export const NotificationSettingsTopicOptions = [
    SubscriptionTopics.food,
    SubscriptionTopics.medication,
    SubscriptionTopics.skinApplication,
    SubscriptionTopics.bathroom,
    SubscriptionTopics.nap,
    SubscriptionTopics.note,
    SubscriptionTopics.pleaseBringReminder
] as const satisfies readonly SubscriptionTopicId[]

export type NotificationSettingsSnapshot = {
    availability: ConfigurableSubscriptions
    preferences: Subscriptions
}

export function copySubscriptions(subscriptions: Subscriptions): Subscriptions {
    return {
        channels: [...subscriptions.channels],
        topics: subscriptions.topics.map((topic) => ({
            channels: [...topic.channels],
            topic: topic.topic
        }))
    }
}

export const NotificationSettingsPushTopics = [
    SubscriptionTopics.intradayDailies,
    SubscriptionTopics.checkin,
    ...NotificationSettingsTopicOptions
] as const satisfies readonly SubscriptionTopicId[]

export class NotificationSettingsService extends UserScopedService {
    readonly #apiClient: ApiClient
    #destroyed = false
    #latestSuccessfulSaveOrdinal = 0
    #nextSaveOrdinal = 0
    #operationController = new AbortController()
    #refreshPromise: Promise<NotificationSettingsSnapshot> | undefined
    #snapshot: NotificationSettingsSnapshot | undefined

    public constructor(scope: UserScopedServiceScope, apiClient: ApiClient) {
        super(scope)
        this.#apiClient = apiClient
    }

    public get cachedSnapshot(): NotificationSettingsSnapshot | undefined {
        return this.#snapshot ? this.#copySnapshot(this.#snapshot) : undefined
    }

    public destroy(): void {
        if (this.#destroyed) {
            return
        }

        this.#destroyed = true
        this.#operationController.abort()
        this.#refreshPromise = undefined
        this.#snapshot = undefined
    }

    public async refresh(): Promise<NotificationSettingsSnapshot> {
        this.#assertAvailable()
        const signal = this.#operationController.signal

        if (this.#refreshPromise) {
            const snapshot = await this.#refreshPromise
            this.#assertActiveOperation(signal)
            return this.#copySnapshot(snapshot)
        }

        const latestSuccessfulSaveOrdinal = this.#latestSuccessfulSaveOrdinal
        const refreshPromise = this.#loadSnapshot(latestSuccessfulSaveOrdinal)
        this.#refreshPromise = refreshPromise

        try {
            const snapshot = await refreshPromise
            this.#assertActiveOperation(signal)
            return this.#copySnapshot(snapshot)
        } finally {
            if (this.#refreshPromise === refreshPromise) {
                this.#refreshPromise = undefined
            }
        }
    }

    async #loadSnapshot(
        latestSuccessfulSaveOrdinal: number
    ): Promise<NotificationSettingsSnapshot> {
        const signal = this.#operationController.signal
        const payload = await this.#apiClient.subscriptions.get({
            signal
        })
        this.#assertActiveOperation(signal)

        const loadedSnapshot: NotificationSettingsSnapshot = {
            availability: this.#copyConfigurableSubscriptions(
                payload.configurableSubscriptions
            ),
            preferences: copySubscriptions(payload.subscriptions)
        }

        const preferences =
            latestSuccessfulSaveOrdinal === this.#latestSuccessfulSaveOrdinal
                ? loadedSnapshot.preferences
                : (this.#snapshot?.preferences ?? loadedSnapshot.preferences)
        this.#snapshot = {
            availability: this.#copyConfigurableSubscriptions(loadedSnapshot.availability),
            preferences: copySubscriptions(preferences)
        }

        return this.#snapshot
    }

    public async save(
        confirmed: Subscriptions,
        next: Subscriptions,
        signal: AbortSignal
    ): Promise<Subscriptions> {
        this.#assertAvailable()

        if (!this.#snapshot) {
            throw new Error('Notification settings must be loaded before they can be saved.')
        }

        const initialAvailability = this.#copyConfigurableSubscriptions(
            this.#snapshot.availability
        )

        const add: SubscriptionsUpdateSet = { channels: [], topics: [] }
        const remove: SubscriptionsUpdateSet = { channels: [], topics: [] }

        const confirmedEmail = confirmed.channels.includes(SubscriptionChannels.email)
        const nextEmail = next.channels.includes(SubscriptionChannels.email)

        if (confirmedEmail !== nextEmail) {
            const operation = nextEmail ? add : remove
            operation.channels.push(SubscriptionChannels.email)
        }

        for (const topic of NotificationSettingsPushTopics) {
            this.#recordTopicChange(
                add,
                remove,
                this.#hasPushTopic(confirmed, topic),
                this.#hasPushTopic(next, topic),
                topic
            )
        }

        const request = this.#createUpdateRequest(add, remove)

        if (request === undefined) {
            return copySubscriptions(next)
        }

        const saveOrdinal = ++this.#nextSaveOrdinal

        const operationSignal = AbortSignal.any([signal, this.#operationController.signal])
        const { subscriptions } = await this.#apiClient.subscriptions.update(request, {
            signal: operationSignal
        })
        this.#assertActiveOperation(operationSignal)
        const preferences = copySubscriptions(subscriptions)

        if (saveOrdinal > this.#latestSuccessfulSaveOrdinal) {
            this.#latestSuccessfulSaveOrdinal = saveOrdinal
            this.#snapshot = {
                availability: this.#copyConfigurableSubscriptions(
                    this.#snapshot?.availability ?? initialAvailability
                ),
                preferences: copySubscriptions(preferences)
            }
        }

        return copySubscriptions(preferences)
    }

    #assertAvailable(): void {
        if (this.#destroyed) {
            throw new Error('Notification settings service has been destroyed.')
        }
    }

    #assertActiveOperation(signal: AbortSignal): void {
        signal.throwIfAborted()
        this.#assertAvailable()
    }

    #copySnapshot(snapshot: NotificationSettingsSnapshot): NotificationSettingsSnapshot {
        return {
            availability: this.#copyConfigurableSubscriptions(snapshot.availability),
            preferences: copySubscriptions(snapshot.preferences)
        }
    }

    #createUpdateRequest(
        add: SubscriptionsUpdateSet,
        remove: SubscriptionsUpdateSet
    ): SubscriptionsUpdateRequest | undefined {
        const hasAdditions = this.#hasChanges(add)
        const hasRemovals = this.#hasChanges(remove)

        if (hasAdditions && hasRemovals) {
            return { add, remove }
        }

        if (hasAdditions) {
            return { add }
        }

        if (hasRemovals) {
            return { remove }
        }

        return undefined
    }

    #copyConfigurableSubscriptions(
        subscriptions: ConfigurableSubscriptions
    ): ConfigurableSubscriptions {
        return {
            channels: subscriptions.channels.map(({ channel }) => ({ channel })),
            topics: subscriptions.topics.map(({ channel, topic }) => ({ channel, topic }))
        }
    }

    #hasChanges(update: SubscriptionsUpdateSet): boolean {
        return update.channels.length > 0 || update.topics.length > 0
    }

    #hasPushTopic(subscriptions: Subscriptions, topic: SubscriptionTopicId): boolean {
        return subscriptions.topics.some(
            (subscription) =>
                subscription.topic === topic &&
                subscription.channels.includes(SubscriptionChannels.push)
        )
    }

    #recordTopicChange(
        add: SubscriptionsUpdateSet,
        remove: SubscriptionsUpdateSet,
        confirmed: boolean,
        next: boolean,
        topic: Exclude<SubscriptionTopicId, typeof SubscriptionTopics.unknown>
    ): void {
        if (confirmed === next) {
            return
        }

        const operation = next ? add : remove
        operation.topics.push({
            channels: [SubscriptionChannels.push],
            topic
        })
    }
}
